import { useState, useEffect, useCallback, useRef } from "react";
import { useTranslation } from "react-i18next";
import { X, ChevronLeft, ChevronRight, RotateCcw, Sparkles, Play, BookOpen } from "lucide-react";

const STORAGE_KEY = "maxemployee.tutorial_completed";

const TUTORIAL_STEPS = [
  {
    target: "[data-tour='stats-row']",
    titleKey: "tutorial.step1.title",
    contentKey: "tutorial.step1.content",
    placement: "bottom",
  },
  {
    target: "[data-tour='agents-section']",
    titleKey: "tutorial.step2.title",
    contentKey: "tutorial.step2.content",
    placement: "top",
  },
  {
    target: "[data-tour='first-contact']",
    titleKey: "tutorial.step3.title",
    contentKey: "tutorial.step3.content",
    placement: "top",
  },
  {
    target: "[data-tour='storage-section']",
    titleKey: "tutorial.step4.title",
    contentKey: "tutorial.step4.content",
    placement: "top",
  },
  {
    target: "[data-tour='credits-section']",
    titleKey: "tutorial.step5.title",
    contentKey: "tutorial.step5.content",
    placement: "top",
  },
  {
    target: "[data-tour='integrations-section']",
    titleKey: "tutorial.step6.title",
    contentKey: "tutorial.step6.content",
    placement: "top",
  },
  {
    target: "body",
    titleKey: "tutorial.step7.title",
    contentKey: "tutorial.step7.content",
    placement: "center",
  },
  {
    target: "body",
    titleKey: "tutorial.step8.title",
    contentKey: "tutorial.step8.content",
    placement: "center",
  },
];

function Spotlight({ targetSelector }) {
  const [style, setStyle] = useState(null);

  useEffect(() => {
    if (!targetSelector || targetSelector === "body") {
      setStyle(null);
      return;
    }
    const el = document.querySelector(targetSelector);
    if (!el) {
      setStyle(null);
      return;
    }
    const update = () => {
      const rect = el.getBoundingClientRect();
      const pad = 6;
      setStyle({
        top: rect.top - pad,
        left: rect.left - pad,
        width: rect.width + pad * 2,
        height: rect.height + pad * 2,
      });
    };
    update();
    window.addEventListener("resize", update);
    window.addEventListener("scroll", update);
    return () => {
      window.removeEventListener("resize", update);
      window.removeEventListener("scroll", update);
    };
  }, [targetSelector]);

  if (!style) return null;

  return (
    <div
      className="fixed z-[9998] pointer-events-none transition-all duration-300"
      style={{
        ...style,
        boxShadow: "0 0 0 9999px rgba(0,0,0,0.75)",
        border: "2px solid rgba(245, 158, 11, 0.6)",
        borderRadius: "6px",
      }}
    />
  );
}

function TutorialTooltip({ step, index, totalSteps, onNext, onPrev, onSkip, isFirst, isLast }) {
  const { t } = useTranslation();
  const [isMobile, setIsMobile] = useState(false);

  useEffect(() => {
    const check = () => setIsMobile(window.innerWidth < 768);
    check();
    window.addEventListener("resize", check);
    return () => window.removeEventListener("resize", check);
  }, []);

  const placement = isMobile ? "bottom" : step.placement;

  return (
    <div
      className="fixed z-[9999] transition-all duration-300 ease-out"
      style={{
        ...(placement === "center"
          ? { top: "50%", left: "50%", transform: "translate(-50%, -50%)" }
          : placement === "bottom"
          ? { top: "50%", left: "50%", transform: "translate(-50%, 0)" }
          : { top: "30%", left: "50%", transform: "translate(-50%, 0)" }),
        width: isMobile ? "calc(100vw - 2rem)" : "440px",
        maxWidth: "440px",
      }}
    >
      <div className="relative bg-zinc-950 border border-zinc-700 shadow-2xl shadow-black/80 rounded-sm">
        {/* Header accent */}
        <div className="h-1 bg-gradient-to-r from-amber-500 to-amber-400" />

        {/* Close */}
        <button
          onClick={onSkip}
          className="absolute top-3 right-3 p-1 text-zinc-500 hover:text-white transition-colors z-10"
          aria-label={t("tutorial.close")}
        >
          <X className="w-4 h-4" />
        </button>

        <div className="p-6">
          {/* Step counter */}
          <div className="font-mono text-[10px] uppercase tracking-[0.3em] text-amber-500 mb-3">
            {t("tutorial.step_label", { current: index + 1, total: totalSteps })}
          </div>

          {/* Title */}
          <h3 className="font-display text-xl uppercase text-white mb-3 pr-6">
            {t(step.titleKey)}
          </h3>

          {/* Content */}
          <p className="text-zinc-400 text-sm leading-relaxed">
            {t(step.contentKey)}
          </p>

          {/* Progress dots */}
          <div className="flex items-center gap-1.5 mt-5 mb-4">
            {Array.from({ length: totalSteps }).map((_, i) => (
              <div
                key={i}
                className={`h-1 transition-all duration-300 ${
                  i === index
                    ? "w-6 bg-amber-500"
                    : i < index
                    ? "w-3 bg-zinc-600"
                    : "w-3 bg-zinc-800"
                }`}
              />
            ))}
          </div>

          {/* Navigation */}
          <div className="flex items-center justify-between">
            <button
              onClick={onSkip}
              className="font-mono text-[10px] uppercase tracking-[0.2em] text-zinc-500 hover:text-zinc-300 transition-colors px-2 py-1"
            >
              {t("tutorial.skip")}
            </button>

            <div className="flex items-center gap-2">
              {!isFirst && (
                <button
                  onClick={onPrev}
                  className="flex items-center gap-1 border border-zinc-700 hover:border-zinc-500 text-zinc-400 hover:text-white px-3 py-2 font-mono text-[10px] uppercase tracking-[0.2em] transition-colors"
                >
                  <ChevronLeft className="w-3.5 h-3.5" />
                  {t("tutorial.prev")}
                </button>
              )}
              <button
                onClick={onNext}
                className="flex items-center gap-1 bg-amber-500 hover:bg-amber-400 text-black px-4 py-2 font-bold uppercase tracking-wider text-[10px] transition-colors"
              >
                {isLast ? (
                  <>
                    <Sparkles className="w-3.5 h-3.5" />
                    {t("tutorial.finish")}
                  </>
                ) : (
                  <>
                    {t("tutorial.next")}
                    <ChevronRight className="w-3.5 h-3.5" />
                  </>
                )}
              </button>
            </div>
          </div>
        </div>

        {/* Arrow */}
        {placement !== "center" && (
          <div
            className={`absolute left-1/2 -translate-x-1/2 ${
              placement === "bottom" ? "-top-2" : "-bottom-2"
            }`}
          >
            <div
              className={`w-4 h-4 bg-zinc-950 border-zinc-700 rotate-45 ${
                placement === "bottom"
                  ? "border-l border-t -translate-y-2"
                  : "border-r border-b translate-y-2"
              }`}
            />
          </div>
        )}
      </div>
    </div>
  );
}

export default function InteractiveTutorial({ onComplete }) {
  const { t } = useTranslation();
  const [isActive, setIsActive] = useState(false);
  const [currentStep, setCurrentStep] = useState(0);
  const [showLauncher, setShowLauncher] = useState(true);

  const totalSteps = TUTORIAL_STEPS.length;
  const step = TUTORIAL_STEPS[currentStep];
  const isFirst = currentStep === 0;
  const isLast = currentStep === totalSteps - 1;

  // Check if tutorial was completed
  useEffect(() => {
    try {
      const completed = localStorage.getItem(STORAGE_KEY);
      if (completed === "true") {
        setShowLauncher(true);
      }
    } catch {
      // ignore
    }
  }, []);

  const startTutorial = useCallback(() => {
    setCurrentStep(0);
    setIsActive(true);
    setShowLauncher(false);
  }, []);

  const closeTutorial = useCallback(() => {
    setIsActive(false);
    setShowLauncher(true);
    try {
      localStorage.setItem(STORAGE_KEY, "true");
    } catch {
      // ignore
    }
    onComplete?.();
  }, [onComplete]);

  const goNext = useCallback(() => {
    if (currentStep < totalSteps - 1) {
      setCurrentStep((s) => s + 1);
    } else {
      closeTutorial();
    }
  }, [currentStep, totalSteps, closeTutorial]);

  const goPrev = useCallback(() => {
    if (currentStep > 0) {
      setCurrentStep((s) => s - 1);
    }
  }, [currentStep]);

  const restartTutorial = useCallback(() => {
    setCurrentStep(0);
    setIsActive(true);
    setShowLauncher(false);
  }, []);

  // Expose restart globally
  useEffect(() => {
    window.__restartInteractiveTutorial = restartTutorial;
    return () => {
      delete window.__restartInteractiveTutorial;
    };
  }, [restartTutorial]);

  // Keyboard navigation
  useEffect(() => {
    if (!isActive) return;
    const handler = (e) => {
      if (e.key === "ArrowRight" || e.key === "Enter") {
        e.preventDefault();
        goNext();
      } else if (e.key === "ArrowLeft") {
        e.preventDefault();
        goPrev();
      } else if (e.key === "Escape") {
        e.preventDefault();
        closeTutorial();
      }
    };
    window.addEventListener("keydown", handler);
    return () => window.removeEventListener("keydown", handler);
  }, [isActive, goNext, goPrev, closeTutorial]);

  return (
    <>
      {/* Launcher button */}
      {showLauncher && !isActive && (
        <button
          onClick={startTutorial}
          className="fixed bottom-6 right-6 z-[100] flex items-center gap-2 bg-amber-500 hover:bg-amber-400 text-black px-5 py-3 font-bold uppercase tracking-wider text-[11px] transition-all shadow-lg shadow-amber-500/20 hover:shadow-amber-500/40 rounded-sm"
        >
          <Play className="w-4 h-4" />
          {t("tutorial.start_button")}
        </button>
      )}

      {/* Active tutorial */}
      {isActive && (
        <>
          {/* Overlay */}
          <div
            className="fixed inset-0 z-[9997] bg-black/60"
            onClick={closeTutorial}
          />

          {/* Spotlight */}
          <Spotlight targetSelector={step.target} />

          {/* Tooltip */}
          <TutorialTooltip
            step={step}
            index={currentStep}
            totalSteps={totalSteps}
            onNext={goNext}
            onPrev={goPrev}
            onSkip={closeTutorial}
            isFirst={isFirst}
            isLast={isLast}
          />
        </>
      )}
    </>
  );
}

// Utility to check if tutorial was completed
export function isTutorialCompleted() {
  try {
    return localStorage.getItem(STORAGE_KEY) === "true";
  } catch {
    return false;
  }
}

// Utility to reset tutorial state
export function resetInteractiveTutorial() {
  try {
    localStorage.removeItem(STORAGE_KEY);
  } catch {
    // ignore
  }
}

// Utility to restart tutorial from outside
export function restartInteractiveTutorial() {
  if (window.__restartInteractiveTutorial) {
    window.__restartInteractiveTutorial();
  }
}
